/* Copyright (c) 2020-present CloudIO Inc */

import { FlexItemItem } from 'componentregistry/components/FlexItem/FlexItem';
import { startCase } from 'lodash-es';
import { Platform } from '../platform/Platform';
import {
  AnyManager,
  BaseComponentItem,
  ComponentManager,
  PropertyContext,
} from '../registry/Component.type';
import { AnyItem, ItemType, Page } from '../registry/ComponentRegistry';
import { getManager, getName } from '../registry/Manager';

export function checkForFixedChild(
  platform: Platform,
  page: Page,
  item: AnyItem,
  childType: ItemType,
  name: string,
  { appUid, pageId }: PropertyContext,
): boolean | string {
  if (childType === 'PulseTip') {
    return true;
  }
  // allow DataStore & Popup under Page
  if (
    item.type === 'Page' &&
    (childType === 'AttachmentRevisions' ||
      childType === 'CloudDataStore' ||
      childType === 'Comments' ||
      childType === 'DataStore' ||
      childType === 'Drawer' ||
      childType === 'EmbedPage' ||
      childType === 'EventSubscription' ||
      childType === 'ExpressionWatcher' ||
      childType === 'FAB' ||
      childType === 'ChatBot' ||
      childType === 'FloatingPanel' ||
      childType === 'Folder' ||
      childType === 'Interval' ||
      childType === 'LocalDataStore' ||
      childType === 'PageHeader' ||
      childType === 'Popup' ||
      childType === 'ReadOnlyDataStore' ||
      childType === 'SidebarPanel' ||
      childType === 'SignaturePad' ||
      childType === 'SpeedDial' ||
      childType === 'SuspendPage' ||
      childType === 'Transaction' ||
      childType === 'TransientDataStore')
  ) {
    return true;
  }
  // allow button, buttonDropdown & icons under tabItem
  if (
    item.type === 'TabItem' &&
    (childType === 'Button' ||
      childType === 'ButtonDropdown' ||
      childType === 'FAB' ||
      childType === 'ChatBot' ||
      childType === 'IconButton' ||
      childType === 'ProgressIndicator' ||
      childType === 'SpeedDial' ||
      childType === 'SuspendPage' ||
      childType === 'Toolbar')
  ) {
    return true;
  }
  // allow all triggers
  const mgr = getManager(childType);
  if (mgr.managerType === 'Trigger') {
    return true;
  }

  if (
    item.type === 'FlexItem' &&
    (item as FlexItemItem).props.collapsible &&
    childType === 'Toolbar'
  ) {
    // allow Toolbar inside collapsible FlexItem
    return true;
  }

  let children = platform.getChildrenItems(
    appUid,
    pageId,
    page,
    item,
    () => true,
  );
  if (item.type === 'Page') {
    // filter out DataStore & Popup for the remaining checks
    children = children.filter(
      (c) =>
        c.type !== 'CloudDataStore' &&
        c.type !== 'Comments' &&
        c.type !== 'DataStore' &&
        c.type !== 'Drawer' &&
        c.type !== 'EmbedPage' &&
        c.type !== 'EventSubscription' &&
        c.type !== 'ExpressionWatcher' &&
        c.type !== 'FAB' &&
        c.type !== 'ChatBot' &&
        c.type !== 'FloatingPanel' &&
        c.type !== 'Folder' &&
        c.type !== 'Interval' &&
        c.type !== 'LocalDataStore' &&
        c.type !== 'PageHeader' &&
        c.type !== 'Popup' &&
        c.type !== 'ReadOnlyDataStore' &&
        c.type !== 'SidebarPanel' &&
        c.type !== 'SpeedDial' &&
        c.type !== 'SuspendPage' &&
        c.type !== 'Transaction' &&
        c.type !== 'TransientDataStore',
    );
  }
  if (item.type === 'TabItem') {
    // filter out Button, ButtonDropdown & IconButton for the remaining checks
    children = children.filter(
      (c) =>
        c.type !== 'Button' &&
        c.type !== 'ButtonDropdown' &&
        c.type !== 'FAB' &&
        c.type !== 'IconButton' &&
        c.type !== 'ChatBot' &&
        c.type !== 'ProgressIndicator' &&
        c.type !== 'SpeedDial' &&
        c.type !== 'SuspendPage' &&
        c.type !== 'Toolbar',
    );
  }
  if (item.type === 'Popup') {
    // filter out PopupActions & Toolbar for the remaining checks
    children = children.filter(
      (c) => c.type !== 'PopupActions' && c.type !== 'Toolbar',
    );
  }
  if (item.type === 'FlexItem') {
    // filter out PopupActions & Toolbar for the remaining checks
    children = children.filter((c) => c.type !== 'Toolbar');
  }
  children = children.filter((c) => c.type !== 'Mirror');

  // filter out non Component children for the remaining checks
  children = children.filter(
    (c) => getManager(c.type).managerType === 'Component',
  );
  if (mgr.managerType === 'Component' && mgr.layout !== 'auto') {
    // only one non-auto layout child is allowed
    return children.length === 0
      ? true
      : `${name} must be empty to add ${mgr.name}!`;
  }

  const hasFixedChild = children.find((c) => {
    const m = getManager(c.type);
    if (item.type === 'FlexItem' && c.type === 'ControllerComponent') {
      return true;
    }
    return m.managerType === 'Component' && m.layout !== 'auto';
  });
  // do not allow additional children when already a fixed layout child exists
  return !hasFixedChild
    ? true
    : `${mgr.name} it not allowed inside ${name} with a non-auto layout child!`;
}

export function preventIfParentPathContainsAutoLayout(
  page: Page,
  item: AnyItem,
  name: string,
): boolean | string {
  let parent = item;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let mgr: ComponentManager<any> | null = getManager(
    parent.type,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as ComponentManager<any>;
  while (
    mgr &&
    mgr.type !== 'Page' &&
    mgr.type !== 'Popover' &&
    mgr.type !== 'AutoSizer'
  ) {
    if (
      mgr.layout === 'auto' ||
      (mgr.type === 'FlexItem' &&
        (parent as FlexItemItem).props.sizeType === 'auto')
    ) {
      // data grid requires size i.e. layout should be either fixed or sizeAware
      return `${name} cannot be added inside '${getName(
        item.type,
      )}' as an auto layout component '${
        mgr.name
      }' existing in the parent path!`;
    }
    parent = page[parent.parent];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mgr = parent ? (getManager(parent.type) as ComponentManager<any>) : null;
  }
  return true;
}

export function hasAutoLayoutParent(
  page: Page,
  item: AnyItem,
): {
  result: boolean;
  parent?: BaseComponentItem;
  mgr?: ComponentManager<never, never>;
} {
  let parent = page[item.parent];
  let mgr = getManager(parent.type) as unknown as ComponentManager<never>;
  while (
    mgr &&
    mgr.type !== 'Page' &&
    mgr.type !== 'Popover' &&
    mgr.type !== 'AutoSizer'
  ) {
    if (
      mgr.layout === 'auto' ||
      (mgr.type === 'FlexItem' &&
        (parent as FlexItemItem).props.sizeType === 'auto')
    ) {
      return { result: true, parent, mgr };
    }
    parent = page[parent.parent];
    mgr = getManager(parent.type) as unknown as ComponentManager<never>;
  }
  return { result: false };
}

export function isAllowedInsideParentDuplicateCheck(
  platform: Platform,
  page: Page,
  item: AnyItem,
  itemType: ItemType,
  { appUid, pageId }: PropertyContext,
  parents: ItemType[] | 'ALL',
): boolean | string {
  const children = platform.getChildrenOfType(
    appUid,
    pageId,
    page,
    item,
    itemType,
  );
  if (
    children.length &&
    (Array.isArray(parents) ? parents?.includes(item.type) : parents === 'ALL')
  ) {
    return `Can have only one '${getName(itemType)}' inside '${getName(
      item.type,
    )}'`;
  }
  return true;
}

export function toOptions(values: string[]): Record<string, string> {
  return values.reduce((options, value) => {
    options[value] = startCase(value);
    return options;
  }, {} as Record<string, string>);
}

export function isChildAllowed(props: {
  page: Page;
  appUid: string;
  pageId: string;
  parentItemId: string;
  childItemId: string;
  platform: Platform;
}): { allowed: string | boolean; m: AnyManager } {
  const { page, appUid, pageId, parentItemId, childItemId, platform } = props;
  const parent = page[parentItemId];
  if (!parent) {
    throw Error(`Missing item with parentItemId ${parentItemId}`);
  }
  const child = page[childItemId];
  if (!child) {
    throw Error(`Missing item with childItemId ${childItemId}`);
  }
  const parentMgr = getManager(parent.type);
  const childMgr = getManager(child.type);
  const allowed = parentMgr.isChildAllowed(page, parent, child.type, {
    appUid,
    pageId,
    itemId: parent.itemId,
    mgr: parentMgr,
    platform,
  });
  if (typeof allowed === 'string' || !allowed) {
    return { allowed, m: childMgr };
  }
  const isAllowedInsideParent = childMgr.isAllowedInsideParent(page, parent, {
    appUid,
    pageId,
    itemId: child.itemId,
    mgr: childMgr,
    platform,
  });
  if (typeof isAllowedInsideParent === 'string') {
    return { allowed: isAllowedInsideParent, m: childMgr };
  }
  return {
    allowed: allowed && isAllowedInsideParent,
    m: childMgr,
  };
}
